import { useEffect, useState } from 'react';
import { ReactMic } from 'react-mic';
import LocalIcon from '../../../ui/LocalIcon';
import { darkGray, lightGray, red } from '../../../utils/style/colors';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import notify from '../../Notify/Notify';
import { IDocumentView } from '../../../store/providers/providerTypes';
import {
  releaseUploadFile,
  rememberUploadFile,
} from 'utils/pendingUploadFiles';

const RecordedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row-reverse;
  gap: 1rem;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;

  & canvas {
    width: 100%;
  }
`;

const StartStopContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  height: 8rem;
  width: 8rem;
  border-radius: 10rem;
  background: rgba(211, 211, 211, 0.2);
  box-shadow: 1px 1px 2px 2px ${lightGray};

  &:hover {
    cursor: pointer;
  }

  ${(props) =>
    props.isRecording &&
    'animation: recording 0.5s ease-in infinite alternate;'}

  @media screen and (min-width: 600px) {
    height: 12rem;
    width: 12rem;
    font-size: 1.3rem;
  }

  @keyframes recording {
    0% {
      transform: scale(1.01);
    }

    100% {
      transform: scale(1.05);
    }
  }
`;

const Status = styled.div``;

const MobileTimer = styled.div`
  font-weight: bold;
  font-size: 1.2rem;
`;

interface IAudioRecorderProps {
  addVoice: (audioUrl: string, size: number, format: string) => void;
  removeVoice: () => void;
  required: boolean;
  maxSize: number;
  file: IDocumentView | null | undefined;
}

const AudioRecorder: React.FC<IAudioRecorderProps> = ({
  addVoice,
  removeVoice,
  required,
  maxSize,
  file,
}) => {
  const { t } = useTranslation('survey');

  const [timer, setTimer] = useState<number>(0);
  const [record, setRecord] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);

  const toggleRecording = () => {
    if (record) {
      setRecord(false);
    } else {
      setRecord(true);
    }
  };

  const onStop = (recordedData: any) => {
    if (recordedData.size > maxSize) {
      notify(t('recordedFileReachedMaximumSize'), 'error');
    }
    if (audioURL) releaseUploadFile(audioURL);
    if (recordedData.blob && recordedData.blobURL) {
      rememberUploadFile(recordedData.blobURL, recordedData.blob);
    }
    setAudioURL(recordedData.blobURL);
    addVoice(
      recordedData.blobURL,
      recordedData.blob.size,
      recordedData.blob.type
    );
  };

  useEffect(() => {
    let recordingInterval: any;
    if (record) {
      recordingInterval = setInterval(() => {
        setTimer((prevState) => prevState + 1);
      }, 1000);
    } else {
      setTimer(0);
    }

    return () => recordingInterval && clearInterval(recordingInterval);
  }, [record]);

  return (
    <div className="p-4 space-y-4">
      <input
        required={required}
        value={audioURL as string}
        style={{ visibility: 'hidden' }}
      />
      <Container>
        <StartStopContainer onClick={toggleRecording} isRecording={record}>
          <Status>
            {record
              ? t('stop')
              : audioURL || file
                ? t('reRecordVoice')
                : t('recordVoice')}
          </Status>
          <MobileTimer>
            {Math.floor(timer / 60)}:{timer - Math.floor(timer / 60) * 60}
          </MobileTimer>
        </StartStopContainer>

        <ReactMic
          record={record}
          onStop={onStop}
          strokeColor={`${darkGray}`}
          backgroundColor="var(--exact-white)"
          mimeType="audio/wav"
          className="flex-1"
        />

        {(audioURL || file) && (
          <RecordedContainer>
            <audio
              controls
              src={audioURL || file?.url}
              controlsList="nodownload"
            />
            <LocalIcon
              iconName={'trash.svg'}
              onClick={() => {
                if (audioURL) releaseUploadFile(audioURL);
                setAudioURL(null);
                removeVoice();
              }}
              isLink={true}
              color={red}
            />
          </RecordedContainer>
        )}
      </Container>
    </div>
  );
};

export default AudioRecorder;
